'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from './select';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  minDate?: Date;
  maxDate?: Date;
  month?: number;
  year?: number;
  selected?: Date;
  isChangeMonth?: boolean;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  minDate,
  maxDate,
  selected,
  isChangeMonth = false,
  ...props
}: CalendarProps) {
  const [selectedMonth, setSelectedMonth] = React.useState(
    selected?.getMonth() ?? maxDate?.getMonth() ?? new Date().getMonth(),
  );
  const [selectedYear, setSelectedYear] = React.useState(
    selected?.getFullYear() ??
      maxDate?.getFullYear() ??
      new Date().getFullYear(),
  );

  const handleMonthChange = (
    event: React.ChangeEvent<HTMLSelectElement> | number,
  ) => {
    const newMonth =
      typeof event === 'number' ? event : parseInt(event.target.value, 10);
    setSelectedMonth(newMonth);
    // Update the DayPicker month when the dropdown changes
    props.onMonthChange &&
      props.onMonthChange(new Date(selectedYear, newMonth));
  };

  const handleYearChange = (
    event: React.ChangeEvent<HTMLSelectElement> | number,
  ) => {
    const newYear =
      typeof event === 'number' ? event : parseInt(event.target.value, 10);
    setSelectedYear(newYear);
    // Update the DayPicker month when the dropdown changes
    props.onYearChange && props.onYearChange(new Date(newYear, selectedMonth));
  };

  return (
    <div className="bg-white">
      <div className="mx-6 flex justify-center space-x-2 pt-2">
        <Select
          value={selectedMonth.toString()}
          onValueChange={(value) => handleMonthChange(parseInt(value, 10))}
        >
          <SelectTrigger className="rounded border bg-white p-2 text-xs focus:outline-none focus:ring-0">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent className="bg-white text-xs">
            {Array.from({ length: 12 }, (_, i) => (
              <SelectItem key={i} value={i.toString()}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={selectedYear.toString()}
          onValueChange={(value) => handleYearChange(parseInt(value, 10))}
        >
          <SelectTrigger className="rounded border bg-white p-2 text-xs focus:outline-none focus:ring-0">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent className="bg-white text-xs">
            {(() => {
              const currentYear = new Date().getFullYear();
              const startYear = minDate?.getFullYear() || currentYear - 5;
              const endYear = maxDate?.getFullYear() || currentYear + 5;
              const years = [];

              for (let year = startYear; year <= endYear; year++) {
                years.push(
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>,
                );
              }
              return years;
            })()}
          </SelectContent>
        </Select>
      </div>
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn('bg-white p-3', className)}
        month={
          selectedMonth ? new Date(selectedYear, selectedMonth) : undefined
        }
        year={selectedYear ? selectedYear : undefined}
        classNames={{
          months:
            'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4',
          caption: 'flex justify-center pt-1 relative items-center',
          caption_label: 'text-sm font-medium',
          nav: 'space-x-1 flex items-center',
          nav_button: cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-white p-0 opacity-50 hover:opacity-100',
          ),
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse space-y-1',
          head_row: 'flex',
          head_cell:
            'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
          row: 'flex w-full mt-2',
          cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected])]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          day: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-9 w-9 p-0 font-normal aria-selected:opacity-100 focus:text-white focus:rounded-full focus:bg-primary',
          ),
          day_range_end: 'day-range-end',
          day_selected:
            'text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground focus:text-white focus:rounded-full',
          day_today: 'bg-accent text-accent-foreground',
          day_outside:
            'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
          day_disabled: 'text-muted-foreground opacity-50',
          day_range_middle:
            'aria-selected:bg-accent aria-selected:text-accent-foreground',
          day_hidden: 'invisible',
          ...classNames,
        }}
        components={{
          IconLeft: () => (
            <div
              onClick={() => {
                if (selectedMonth === 0) {
                  handleMonthChange(11);
                  handleYearChange(selectedYear - 1);
                  // Optionally, you can also handle year change here if needed
                } else {
                  handleMonthChange(selectedMonth - 1);
                }
              }}
            >
              <ChevronLeft className="h-4 w-4" aria-label="Previous month" />
            </div>
          ),
          IconRight: () => (
            <div
              onClick={() => {
                if (selectedMonth === 11) {
                  handleMonthChange(0);
                  handleYearChange(selectedYear + 1);
                  // Optionally, you can also handle year change here if needed
                } else {
                  handleMonthChange(selectedMonth + 1);
                }
              }}
            >
              <ChevronRight className="h-4 w-4" aria-label="Next month" />
            </div>
          ),
        }}
        {...props}
        selected={selected}
        {...(minDate && { fromDate: minDate })}
        {...(maxDate && { toDate: maxDate })}
      />
    </div>
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
